import React, { useState, useRef, useEffect } from 'react';
import { HelpCircle } from 'lucide-react';

const helpData = {
    aws: {
        'Cloud Provider': { purpose: 'Select the cloud environment where your resources reside.', find: null, example: null, link: null },
        'Access Key ID': { 
            purpose: 'AWS programmatic access identifier.', 
            find: ['Open AWS IAM Console', 'Navigate to "Users" and select your user', 'Go to "Security credentials" tab', 'Click "Create access key"'], 
            example: 'AKIA************',
            link: 'https://docs.aws.amazon.com/IAM/latest/UserGuide/id_credentials_access-keys.html'
        },
        'Secret Access Key': { 
            purpose: 'Used with Access Key ID for authentication.', 
            find: ['Displayed only once upon Access Key creation', 'Download the .csv file or copy it immediately', 'If lost, you must create a new key pair'], 
            example: 'wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY',
            link: 'https://docs.aws.amazon.com/IAM/latest/UserGuide/id_credentials_access-keys.html'
        },
        'Region': { 
            purpose: 'Geographical area for AWS resources.', 
            find: ['Open AWS Management Console', 'Look at the top-right corner of the navigation bar', 'Click the region name to see the region code'], 
            example: 'us-east-1',
            link: 'https://docs.aws.amazon.com/AWSEC2/latest/UserGuide/using-regions-availability-zones.html'
        }
    },
    gcp: {
        'Cloud Provider': { purpose: 'Select the cloud environment where your resources reside.', find: null, example: null, link: null },
        'Project ID': { 
            purpose: 'Unique Google Cloud project identifier.', 
            find: ['Open Google Cloud Console', 'Click the project drop-down at the top', 'Find the "ID" column next to your project name'], 
            example: 'my-prod-project-123456',
            link: 'https://cloud.google.com/resource-manager/docs/creating-managing-projects'
        },
        'Service Account Key': { 
            purpose: 'Authentication file for GCP services.', 
            find: ['Open Google Cloud Console', 'Go to IAM & Admin → Service Accounts', 'Select a service account', 'Go to "Keys" tab → Add Key → Create new key (JSON)'], 
            example: 'JSON file download',
            link: 'https://cloud.google.com/iam/docs/keys-create-delete'
        },
        'Service Account JSON': { 
            purpose: 'Authentication file for GCP services.', 
            find: ['Open Google Cloud Console', 'Go to IAM & Admin → Service Accounts', 'Select a service account', 'Go to "Keys" tab → Add Key → Create new key (JSON)'], 
            example: 'Paste full JSON content here',
            link: 'https://cloud.google.com/iam/docs/keys-create-delete'
        },
        'Region': { 
            purpose: 'Geographical area for GCP resources.', 
            find: ['Open Google Cloud Console', 'Go to Compute Engine → VM instances', 'Note the "Zone" (e.g., us-central1-a)', 'The region drops the last letter'], 
            example: 'us-central1',
            link: 'https://cloud.google.com/compute/docs/regions-zones'
        }
    },
    azure: {
        'Cloud Provider': { purpose: 'Select the cloud environment where your resources reside.', find: null, example: null, link: null },
        'Subscription ID': { 
            purpose: 'Azure subscription identifier.', 
            find: ['Open Azure Portal', 'Search for and select "Subscriptions"', 'Copy the "Subscription ID" column value'], 
            example: '12345678-1234-1234-1234-123456789abc',
            link: 'https://learn.microsoft.com/en-us/azure/azure-portal/get-subscription-tenant-id'
        },
        'Tenant ID': { 
            purpose: 'Azure Active Directory tenant identifier.', 
            find: ['Open Azure Portal', 'Search for "Microsoft Entra ID"', 'Under "Overview", copy the "Tenant ID"'], 
            example: '87654321-4321-4321-4321-cba987654321',
            link: 'https://learn.microsoft.com/en-us/entra/fundamentals/how-to-find-tenant'
        },
        'Client ID': { 
            purpose: 'Application identifier.', 
            find: ['Open Microsoft Entra ID', 'Go to "App registrations" and select your app', 'Copy the "Application (client) ID"'], 
            example: '11111111-2222-3333-4444-555555555555',
            link: 'https://learn.microsoft.com/en-us/entra/identity-platform/howto-create-service-principal-portal'
        },
        'Client Secret': { 
            purpose: 'Authentication secret for the application.', 
            find: ['Open Microsoft Entra ID → App registrations', 'Select your app → "Certificates & secrets"', 'Create a new client secret and copy its "Value"'], 
            example: 'abcDEF123~xyz...',
            link: 'https://learn.microsoft.com/en-us/entra/identity-platform/howto-create-service-principal-portal#option-2-create-a-new-application-secret'
        },
        'Region': { 
            purpose: 'Geographical area for Azure resources.', 
            find: ['Open Azure Portal', 'Go to "Virtual Networks" or your target resource', 'Look at the "Location" property'], 
            example: 'eastus',
            link: 'https://azure.microsoft.com/en-us/explore/global-infrastructure/geographies/'
        }
    }
};

export default function ContextHelp({ field, provider = 'aws' }) {
    const [isOpen, setIsOpen] = useState(false);
    const popupRef = useRef(null);
    const iconRef = useRef(null);

    // Default to the correct provider's data, fallback to aws if not found
    const providerData = helpData[provider] || helpData.aws;
    const content = providerData[field];

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (isOpen && popupRef.current && !popupRef.current.contains(e.target) && !iconRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    if (!content) return null;

    return (
        <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', marginLeft: '6px', verticalAlign: 'middle', zIndex: isOpen ? 100000 : 'auto' }}>
            <HelpCircle 
                ref={iconRef}
                size={14} 
                style={{ color: isOpen ? 'var(--primary, #a855f7)' : 'var(--text-muted, #94a3b8)', cursor: 'pointer', transition: 'color 0.2s' }} 
                onClick={(e) => {
                    e.preventDefault();
                    setIsOpen(!isOpen);
                }} 
            />
            
            {isOpen && (
                <div ref={popupRef} style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: '-10px',
                    transform: 'translateY(-10px)',
                    width: '320px',
                    background: 'var(--bg-primary, #ffffff)',
                    border: '1px solid var(--border-color, #334155)',
                    borderRadius: '8px',
                    padding: '12px',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
                    zIndex: 100000,
                    fontSize: '0.8rem',
                    color: 'var(--text-main, #f8fafc)',
                    lineHeight: '1.4',
                    textAlign: 'left'
                }}>
                    <div style={{ fontWeight: 600, color: 'var(--primary, #a855f7)', marginBottom: '8px', borderBottom: '1px solid var(--border-color, #334155)', paddingBottom: '4px' }}>
                        {field}
                    </div>
                    
                    {content.purpose && (
                        <div style={{ marginBottom: '8px' }}>
                            <span style={{ color: 'var(--text-muted, #94a3b8)', fontWeight: 600 }}>Purpose:</span><br/>
                            {content.purpose}
                        </div>
                    )}
                    
                    {content.find && (
                        <div style={{ marginBottom: '8px' }}>
                            <span style={{ color: 'var(--text-muted, #94a3b8)', fontWeight: 600 }}>How to find it:</span>
                            <ol style={{ margin: '4px 0 0 0', paddingLeft: '1.25rem', color: 'var(--text-main, #f8fafc)' }}>
                                {Array.isArray(content.find) ? content.find.map((step, i) => (
                                    <li key={i} style={{ marginBottom: '2px' }}>{step}</li>
                                )) : <li>{content.find}</li>}
                            </ol>
                        </div>
                    )}
                    
                    {content.example && (
                        <div style={{ marginBottom: '6px' }}>
                            <span style={{ color: 'var(--text-muted, #94a3b8)', fontWeight: 600 }}>Example:</span><br/>
                            <span style={{ fontFamily: 'monospace', color: '#10b981' }}>{content.example}</span>
                        </div>
                    )}
                    
                    {content.link && (
                        <div style={{ marginTop: '10px', paddingTop: '8px', borderTop: '1px solid var(--border-color, #334155)' }}>
                            <a href={content.link} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary, #a855f7)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '0.75rem', fontWeight: 500 }}>
                                Official Documentation <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg>
                            </a>
                        </div>
                    )}
                </div>
            )}
        </span>
    );
}
